// A GIF89a encoder for 1-BIT pages, and nothing else.
//
// WHY WRITE ONE. Every general GIF library carries quantisation, dithering and
// a palette optimiser — all of which this app has already done, better, and in
// its own voice. What is left after that is a two-colour palette and LZW, which
// is about a hundred lines. The dependency would have been larger than the
// feature.
//
// It also happens that a 1-bit dither is close to the ideal GIF payload: two
// colours, no transparency, no palette to negotiate. Measured on a real page at
// the app's full 1080x1428 export size, one frame is ~50KB, so a twelve-frame
// fax is around 0.6MB — small enough to send without thinking about it.
//
// Frames arrive as ONE BYTE PER PIXEL, values 0 or 1, which is exactly what
// `toBytes()` in dither.js already produces. 0 is paper, 1 is ink.

const HEADER = [0x47, 0x49, 0x46, 0x38, 0x39, 0x61]; // "GIF89a"

/** Grows itself; `Uint8Array` cannot, and a page is not a known size up front. */
class Bytes {
  constructor() { this.buf = new Uint8Array(1 << 16); this.n = 0; }

  push(b) {
    if (this.n === this.buf.length) {
      const next = new Uint8Array(this.buf.length * 2);
      next.set(this.buf);
      this.buf = next;
    }
    this.buf[this.n++] = b;
  }

  pushAll(arr) { for (const b of arr) this.push(b); }
  u16(v) { this.push(v & 0xff); this.push((v >> 8) & 0xff); }
  done() { return this.buf.subarray(0, this.n); }
}

/**
 * LZW, as GIF specifies it rather than as the patent described it.
 *
 * The minimum code size is 2 even for a two-colour image: the spec forbids 1,
 * because clear and end-of-information need codes of their own and a 1-bit
 * table has no room for them.
 */
function lzw(indices, minCodeSize = 2) {
  const out = new Bytes();
  const clear = 1 << minCodeSize;        // 4
  const eoi = clear + 1;                 // 5
  let codeSize = minCodeSize + 1;        // 3
  let next = eoi + 1;                    // 6
  let dict = new Map();

  let bitBuf = 0;
  let bitCount = 0;
  const chunk = [];

  const flushChunk = () => {
    while (chunk.length) {
      const take = Math.min(255, chunk.length);
      out.push(take);
      for (let i = 0; i < take; i++) out.push(chunk[i]);
      chunk.splice(0, take);
    }
  };
  const emit = (code) => {
    bitBuf |= code << bitCount;
    bitCount += codeSize;
    while (bitCount >= 8) {
      chunk.push(bitBuf & 0xff);
      bitBuf >>= 8;
      bitCount -= 8;
      if (chunk.length === 255) flushChunk();
    }
  };
  const reset = () => {
    dict = new Map();
    codeSize = minCodeSize + 1;
    next = eoi + 1;
  };

  emit(clear);
  reset();

  let prefix = indices[0];
  for (let i = 1; i < indices.length; i++) {
    const k = indices[i];
    const key = prefix * 4096 + k;   // both fit; faster than a string key
    const found = dict.get(key);
    if (found !== undefined) { prefix = found; continue; }

    emit(prefix);
    dict.set(key, next);
    next += 1;
    // The table grew past what the current code width can address.
    if (next > (1 << codeSize)) {
      if (codeSize < 12) {
        codeSize += 1;
      } else {
        // 12 bits is the ceiling: start the table again, which every decoder
        // knows to do when it sees a clear code.
        emit(clear);
        reset();
      }
    }
    prefix = k;
  }

  emit(prefix);
  emit(eoi);

  if (bitCount > 0) chunk.push(bitBuf & 0xff);
  flushChunk();
  out.push(0);            // block terminator
  return out.done();
}

/**
 * @param {object} o
 * @param {number} o.width
 * @param {number} o.height
 * @param {Uint8Array[]} o.frames  one byte per pixel, 0 or 1
 * @param {number} [o.delayMs]     per frame; GIF stores hundredths of a second
 * @param {number[]} [o.delaysMs]  per-frame delays, when they differ
 * @param {number[]} [o.paper]     RGB for 0
 * @param {number[]} [o.ink]       RGB for 1
 * @returns {Blob} image/gif
 */
export function encodeGif({
  width, height, frames, delayMs = 100, delaysMs = null,
  paper = [255, 255, 255], ink = [0, 0, 0],
}) {
  if (!frames?.length) throw new Error('encodeGif needs at least one frame');
  const out = new Bytes();

  out.pushAll(HEADER);
  out.u16(width);
  out.u16(height);
  // Global colour table present, 8-bit colour resolution, table of 2 entries.
  out.push(0b1_111_0_000);
  out.push(0);            // background index
  out.push(0);            // pixel aspect ratio: none given
  out.pushAll(paper);
  out.pushAll(ink);

  // Loop forever. NETSCAPE2.0 is not in the GIF spec at all — it is a 1995
  // extension every decoder implements and none document. Without it an
  // animation plays once and stops on its last frame.
  if (frames.length > 1) {
    out.pushAll([0x21, 0xff, 0x0b]);
    out.pushAll([...'NETSCAPE2.0'].map((c) => c.charCodeAt(0)));
    out.pushAll([0x03, 0x01, 0x00, 0x00, 0x00]);
  }

  frames.forEach((px, i) => {
    const cs = Math.max(2, Math.round((delaysMs?.[i] ?? delayMs) / 10));
    out.pushAll([0x21, 0xf9, 0x04]);
    // Disposal 1 (leave in place). Every frame here is a full page, so nothing
    // needs restoring between them.
    out.push(0b000_001_0_0);
    out.u16(cs);
    out.push(0);          // transparent index: unused
    out.push(0);          // block terminator

    out.push(0x2c);       // image descriptor
    out.u16(0); out.u16(0);
    out.u16(width); out.u16(height);
    out.push(0);          // no local table, not interlaced

    out.push(2);          // LZW minimum code size
    out.pushAll(lzw(px, 2));
  });

  out.push(0x3b);         // trailer
  return new Blob([out.done()], { type: 'image/gif' });
}

/**
 * The same file, written a frame at a time.
 *
 * `encodeGif` wants every frame's indices up front: at 1.5MB a frame, forty
 * frames is 60MB of Uint8Array sitting in memory while the page renders — and
 * iOS answers memory pressure by handing back blank canvases, silently. The
 * writer compresses each frame as it arrives (~50KB a frame) and drops the
 * indices, so the peak is one frame regardless of how many there are.
 */
export class GifWriter {
  constructor({ width, height, paper = [255, 255, 255], ink = [0, 0, 0], loop = true }) {
    this.width = width; this.height = height; this.out = new Bytes(); this.n = 0;
    this.out.pushAll(HEADER); this.out.u16(width); this.out.u16(height);
    this.out.push(0b1_111_0_000); this.out.push(0); this.out.push(0);
    this.out.pushAll(paper); this.out.pushAll(ink);
    if (loop) {
      this.out.pushAll([0x21, 0xff, 0x0b]);
      this.out.pushAll([...'NETSCAPE2.0'].map((c) => c.charCodeAt(0)));
      this.out.pushAll([0x03, 0x01, 0x00, 0x00, 0x00]);
    }
  }

  addFrame(indices, delayMs = 100) {
    const cs = Math.max(2, Math.round(delayMs / 10));
    this.out.pushAll([0x21, 0xf9, 0x04]); this.out.push(0b000_001_0_0); this.out.u16(cs); this.out.push(0); this.out.push(0);
    this.out.push(0x2c); this.out.u16(0); this.out.u16(0); this.out.u16(this.width); this.out.u16(this.height); this.out.push(0);
    this.out.push(2); this.out.pushAll(lzw(indices, 2));
    this.n += 1;
  }

  finish() {
    if (!this.n) throw new Error('GifWriter.finish() with no frames');
    this.out.push(0x3b);
    return new Blob([this.out.done()], { type: 'image/gif' });
  }
}
